import {
  useState,
  useCallback,
  useMemo,
  ChangeEvent,
  useRef,
  useEffect,
  forwardRef,
  Fragment,
} from 'react';
import { CircularProgress, TextField, Autocomplete } from '@mui/material';
import { AutocompleteInputChangeReason } from '@mui/material/useAutocomplete';
import {
  SearchOutlined as SearchOutlinedIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { debounce } from 'es-toolkit/compat';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/capacitor';
import { useMapState } from 'state/useMapState';
import { MapLocation, MapSearchApi } from 'shared/types';
import { MapSearchResult } from 'hooks/useMapSearch/useMapSearch.types';
import { useMapSearchState } from 'state/useMapSearchState';
import {
  getMapSearchResultFormattedAddress,
  getMapSearchResultName,
  mapSearchResultToMapLocation,
  getRandomId,
  getLatLngLocationName,
} from 'shared/utils';
import { useLayersDrawerState } from 'features/mapping/hooks/useLayersDrawerState';
import {
  getGoogleCoords,
  getHighlightedSearchMatch,
  getLatLngFromString,
} from 'hooks/useMapSearch';
import {
  getWildfireGeoEventIcon,
  getOpacityForGeoEventMarker,
} from 'features/mapping/icons';
import { useSelectedGeoEventId } from 'features/geoEvents/hooks/useSelectedGeoEventId';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { geoEventToMapSearchResult } from './utils';
import { useStyles } from './styles';
import { fetchGeoEvents, fetchLocations } from './api';
import { SearchOptionItem } from './SearchOptionItem';

/**
 * It's safe to ignore the following warning
 * "Material-UI: The value provided to Autocomplete is invalid.
 * None of the options match with...", we could fix it by adding
 * the "freeSolo" property to the autocomplete component, but that
 * would get rid of the "No Results found" message.
 */

type SearchBarProps = {
  onLocationChange?: (locationDetails: MapLocation | null) => void;
  formField?: boolean;
  label?: string;
  error?: boolean;
  onBlur?: () => void;
  initialSearchTerm?: string;
  name?: string;
  includeGeoEvents?: boolean;
  searchApi?: MapSearchApi;
  disabled?: boolean;
  embedded?: boolean;
  ignoreSelectedGeoEventId?: boolean;
};

type EndAdornmentProps = {
  loading: boolean;
  clearable: boolean;
  onClear: () => void;
  size?: 'small' | 'big';
};

const MIN_SEARCH_TERM_LENGTH = 3;

const EndAdornment = (props: EndAdornmentProps): JSX.Element | null => {
  const { loading, clearable, onClear, size = 'small' } = props;
  const { classes, cx } = useStyles();

  if (loading) {
    return <CircularProgress color="inherit" size={size === 'big' ? 20 : 14} />;
  }

  if (clearable) {
    return (
      <CancelIcon
        className={cx(
          classes.icon,
          classes.pointer,
          size === 'big' && classes.iconFullView,
        )}
        onClick={(e) => {
          e.stopPropagation();
          onClear();
        }}
        data-testid="clear-search"
      />
    );
  }

  return null;
};

/**
 * @deprecated by `useMapSearch()`. This component should be removed soon, and the
 * incident create/edit form search should be reimplemented using `useMapSearch()`.
 */
export const SearchBar = forwardRef(
  (props: SearchBarProps, ref): JSX.Element => {
    const {
      onLocationChange,
      formField = false,
      label,
      error = false,
      onBlur,
      initialSearchTerm = '',
      name = 'noAutoFill',
      includeGeoEvents = false,
      searchApi = 'mapbox',
      disabled = false,
      embedded = false,
      ignoreSelectedGeoEventId = false,
    } = props;
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
    const { value, results, setSearchResults, setSelectedValue, reset } =
      useMapSearchState();
    const { classes, cx } = useStyles();
    const { t } = useTranslation();
    const { incidentMapState } = useMapState();
    const history = useHistory();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const selectedGeoEventId = useSelectedGeoEventId();
    const { open: isLayersDrawerOpen } = useLayersDrawerState();
    const pinImprovements = useReleaseFlag('pin_drop_improvements');

    useEffect(() => {
      return () => {
        reset();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (initialSearchTerm) {
        // Update search map state with the initial search term
        setSelectedValue({
          id: '',
          type: 'Feature',
          address: '',
          text: initialSearchTerm,
          placeName: '',
          center: [],
        });
      }
    }, [setSelectedValue, initialSearchTerm]);

    const currentMapLat = incidentMapState.center.lat;
    const currentMapLng = incidentMapState.center.lng;

    const selectLatLngLocation = useCallback(
      (lat: number, lng: number) => {
        if (!onLocationChange) return;

        const locName = getLatLngLocationName(lat, lng);

        setSelectedValue({
          id: getRandomId().toString(),
          type: 'LatLngLocation',
          text: locName,
          placeName: '',
          center: [lng, lat],
        });

        inputRef.current?.blur();

        onLocationChange({
          name: locName,
          lat,
          lng,
          ...(pinImprovements ? {} : { place: false }),
          type: 'LatLngLocation',
        });
      },
      [onLocationChange, setSelectedValue, pinImprovements],
    );

    const searchLocations = useCallback(
      async (term: string) => {
        const parsedSearchTerm = getLatLngFromString(term) || term;

        if (typeof parsedSearchTerm !== 'string') {
          selectLatLngLocation(parsedSearchTerm.lat, parsedSearchTerm.lng);
          return;
        }

        if (loading) return;

        setSearchResults([]);

        if (!term || term.length < MIN_SEARCH_TERM_LENGTH) return;

        setLoading(true);
        try {
          const proximity = `${currentMapLng},${currentMapLat}`;
          let data = [];

          if (includeGeoEvents) {
            const [res1, res2] = await Promise.allSettled([
              fetchGeoEvents(term),
              fetchLocations(term, proximity, searchApi),
            ]);

            const geoEvents = res1.status === 'fulfilled' ? res1.value : [];
            const locations = res2.status === 'fulfilled' ? res2.value : [];

            const geoEventLocations = geoEvents.map(geoEventToMapSearchResult);
            data = geoEventLocations.concat(locations);
          } else {
            data = await fetchLocations(term, proximity, searchApi);
          }

          setSearchResults(data);
          if (!open && !value?.id) setOpen(true);
        } catch (err) {
          Sentry.captureException(err);
        }
        setLoading(false);
      },
      [
        loading,
        setSearchResults,
        selectLatLngLocation,
        currentMapLng,
        currentMapLat,
        includeGeoEvents,
        open,
        value?.id,
        searchApi,
      ],
    );

    const handleSearchLocations = useMemo(
      () => debounce(searchLocations, 500),
      [searchLocations],
    );

    useEffect(() => {
      return () => {
        handleSearchLocations.cancel();
      };
    }, [handleSearchLocations]);

    const handleSearchTermChange = (
      newInputValue: string,
      reason: AutocompleteInputChangeReason,
    ): void => {
      setSearchTerm(newInputValue);
      if (reason === 'input') {
        // Only trigger a search when the user is typing
        handleSearchLocations(newInputValue);
      }
    };

    const handleOnValueChange = async (
      _: ChangeEvent<object>,
      newValue: string | MapSearchResult | null,
    ): Promise<void> => {
      if (typeof newValue === 'string') return;

      if (newValue === null) {
        setSelectedValue(newValue);
        return;
      }

      let mapLocation: MapSearchResult = { ...newValue };

      if (mapLocation.type === 'GeoEvent') {
        history.push(`/i/${mapLocation.id}`);
        return;
      }

      if (mapLocation.center.length === 0) {
        // Map search result is a google place prediction, get place by id
        const response = await getGoogleCoords(mapLocation.id);
        if (response) {
          mapLocation = {
            ...mapLocation,
            center: response.coords,
            placeAddress: response.formattedAddress,
          };
        }
      }

      setSelectedValue(mapLocation);

      if (mapLocation && onLocationChange) {
        inputRef.current?.blur();
        onLocationChange(mapSearchResultToMapLocation(mapLocation));
      }
    };

    const handleClear = (): void => {
      reset();
      setSearchTerm('');
      setOpen(false);
      if (onLocationChange) onLocationChange(null);
    };

    const handleOpen = (): void => {
      if (results.length) {
        setOpen(true);
      }
    };

    const getPlaceholder = (): string | undefined => {
      if (formField) return undefined;

      if (includeGeoEvents) {
        return t('searchBar.geoEventsPlaceholder');
      }
      return t('searchBar.placeholder');
    };

    return (
      <Autocomplete
        classes={{
          root: cx(
            classes.root,
            open && !loading && !formField && classes.rootOpen,
            formField && classes.rootFormField,
            embedded && classes.rootEmbedded,
          ),
          inputRoot: cx(
            classes.inputRoot,
            formField && classes.inputRootFormField,
          ),
          input: cx(classes.input, formField && classes.inputFormField),
          popper: cx(loading && classes.hide),
          paper: cx(classes.paper, formField && classes.paperFormField),
          noOptions: classes.noOptions,
          option: classes.option,
        }}
        ref={containerRef}
        open={open}
        value={value}
        onChange={handleOnValueChange}
        onOpen={handleOpen}
        onClose={(): void => setOpen(false)}
        isOptionEqualToValue={(option, selectedValue): boolean =>
          option.id === selectedValue.id
        }
        getOptionLabel={(option): string => {
          if (typeof option === 'string') return '';
          return getMapSearchResultName(option);
        }}
        options={searchTerm ? results : []}
        loading={loading}
        filterOptions={(options): MapSearchResult[] => options}
        noOptionsText={
          searchTerm.length >= MIN_SEARCH_TERM_LENGTH &&
          t('searchBar.noResults')
        }
        clearOnBlur={false}
        onInputChange={(_, newInputValue, reason): void =>
          handleSearchTermChange(newInputValue, reason)
        }
        // Needed for "clear" input use case
        inputValue={searchTerm}
        onBlur={() => {
          if (onBlur) onBlur();
        }}
        // Fixes bug where the input is focused when selecting a geo event or opening the
        // layers/legend drawer causing the bottom sheet modal to open on mobile (iOS only).
        disabled={
          disabled ||
          (!ignoreSelectedGeoEventId && Boolean(selectedGeoEventId)) ||
          isLayersDrawerOpen
        }
        renderInput={(params): JSX.Element => (
          <TextField
            {...params}
            name={name}
            inputRef={formField ? ref : inputRef}
            variant={formField ? 'outlined' : 'standard'}
            label={label}
            placeholder={getPlaceholder()}
            error={error}
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: formField ? undefined : (
                  <SearchOutlinedIcon className={classes.icon} />
                ),
                endAdornment: !searchTerm ? null : (
                  <EndAdornment
                    loading={loading}
                    clearable={
                      results.length > 0 ||
                      searchTerm.length >= MIN_SEARCH_TERM_LENGTH
                    }
                    onClear={handleClear}
                    size={formField ? 'big' : 'small'}
                  />
                ),
                ...(formField ? {} : { disableUnderline: true }),
              },
            }}
          />
        )}
        renderOption={(optionProps, option): JSX.Element => {
          const optionName = getMapSearchResultName(option);
          const icon =
            option.type === 'GeoEvent' && option.original
              ? getWildfireGeoEventIcon(option.original)
              : undefined;
          const opacity =
            option.type === 'GeoEvent' && option.original
              ? getOpacityForGeoEventMarker({
                  geoEvent: option.original,
                  isFadable: true,
                })
              : 1;
          const { key, ...restProps } = optionProps;

          return (
            <SearchOptionItem
              key={`${key}-${option.id}`}
              optionName={optionName}
              primaryText={getHighlightedSearchMatch(searchTerm, optionName)}
              secondaryText={getMapSearchResultFormattedAddress(
                optionName,
                option.placeName,
              )}
              optionProps={restProps}
              imgAlt={option.text}
              icon={icon}
              opacity={opacity}
            />
          );
        }}
        groupBy={() => ''}
        renderGroup={(params) => {
          return (
            <Fragment key="render-group">
              <li>
                <ul style={{ paddingLeft: 0 }}>{params.children}</ul>
              </li>
            </Fragment>
          );
        }}
      />
    );
  },
);

SearchBar.displayName = 'SearchBar';
